import React, { useState, useMemo } from 'react';
import { Modal, Button, Checkbox } from 'antd';
import { CloseOutlined, LeftOutlined } from '@ant-design/icons';
import type { IAdBudgetDisplay, IPracticeByProviderRow } from 'types/marketing';
import type { SpecialtyOverrides } from 'pages/dashboard/Marketing/components/GroupSpecialtyOverrides';
import { savePracticePoolBudget } from 'api/marketing';
import { buildPracticeSaveRequest } from 'utils/marketing';
import { isDevEnv } from 'utils/common';
import useGetOffForProlisting from 'hooks/useGetOffForProlisting';
import KlarityLogo from 'assets/common/klarity.svg';
import s from './s.module.less';

const PlatformAgreementUrl = isDevEnv()
    ? 'https://saas-dev-jp.s3.ap-northeast-1.amazonaws.com/dev/ProListingTermsCommonQuestions.html'
    : 'https://saas-prod-us.s3.us-east-2.amazonaws.com/prod/agreement/ProListingTermsCommonQuestions.html';

interface Props {
    open: boolean;
    onCancel: () => void;
    strategyData: IAdBudgetDisplay;
    providers: IPracticeByProviderRow[];
    budgetExcluded: Set<number>;
    stdListingCount: number;
    overrides?: SpecialtyOverrides;
    onConfirm: () => void;
}

interface SpecGroup {
    key: string;
    name: string;
    providers: IPracticeByProviderRow[];
}

const GroupConfirmSaveModal: React.FC<Props> = ({
    open,
    onCancel,
    strategyData,
    providers,
    budgetExcluded,
    stdListingCount,
    overrides,
    onConfirm,
}) => {
    const [agreed, setAgreed] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showAgreement, setShowAgreement] = useState(false);
    const [breakdownOpen, setBreakdownOpen] = useState(false);
    const [off] = useGetOffForProlisting();

    const activeProviders = useMemo(
        () => providers.filter((p) => !budgetExcluded.has(p.providerId)),
        [providers, budgetExcluded],
    );

    const hasAdvanced = strategyData.enableAdvancedSettings && overrides;

    const specGroups: SpecGroup[] = useMemo(() => {
        // Only show specialties served by at least one active provider
        const activeSpecNames = new Set<string>();
        activeProviders.forEach((p) => {
            p.services?.forEach((svc) => {
                if (svc.specialtyName) activeSpecNames.add(svc.specialtyName);
            });
        });

        // Within each shown specialty, include ALL providers (active + inactive)
        const map = new Map<string, IPracticeByProviderRow[]>();
        providers.forEach((p) => {
            p.services
                ?.filter((svc) => svc.specialtyName && activeSpecNames.has(svc.specialtyName))
                .forEach((svc) => {
                    const name = svc.specialtyName;
                    if (!map.has(name)) map.set(name, []);
                    if (!map.get(name)!.some((existing) => existing.providerId === p.providerId)) {
                        map.get(name)!.push(p);
                    }
                });
        });
        return Array.from(map.entries()).map(([name, provs]) => ({
            key: name.toLowerCase().replace(/\s+/g, '_'),
            name,
            providers: provs,
        }));
    }, [activeProviders, providers]);

    const periodLabel = strategyData.budgetPeriod === 'MONTHLY' ? 'mo' : 'wk';
    const periodText = strategyData.budgetPeriod === 'MONTHLY' ? 'monthly' : 'weekly';

    const activeSpecCount = useMemo(() => {
        if (!hasAdvanced) return 0;
        return specGroups.filter((g) => !overrides.disabledSpecs.has(g.key)).length;
    }, [specGroups, hasAdvanced, overrides]);

    // Compute bid range across all specialties and providers
    const bidRange = useMemo(() => {
        const baseBid = strategyData.generalBidAmount;
        if (!hasAdvanced) return { min: baseBid, max: baseBid };

        const bids: number[] = [baseBid];

        specGroups.forEach((g) => {
            if (overrides.disabledSpecs.has(g.key)) return;
            const specBid = overrides.specBids[g.name] ?? baseBid;

            const specProvBids = overrides.provBids[g.key];
            const hasProvOverrides = specProvBids && g.providers.some((p) => specProvBids[p.providerId] !== undefined);
            if (hasProvOverrides) {
                g.providers.forEach((p) => {
                    if (budgetExcluded.has(p.providerId)) return;
                    if (overrides.disabledProvSpecs[g.key]?.has(p.providerId)) return;
                    bids.push(specProvBids[p.providerId] ?? specBid);
                });
            } else {
                bids.push(specBid);
            }
        });

        return { min: Math.min(...bids), max: Math.max(...bids) };
    }, [strategyData.generalBidAmount, hasAdvanced, specGroups, overrides, budgetExcluded]);

    const bidVaries = bidRange.min !== bidRange.max;

    const variesReason = useMemo(() => {
        if (!bidVaries || !hasAdvanced) return '';
        const hasProvOverrides = Object.values(overrides.provBids).some((m) => Object.keys(m).length > 0);
        if (hasProvOverrides) return 'varies by provider';
        return 'varies by specialty';
    }, [bidVaries, hasAdvanced, overrides]);

    const hasSpecCustomizations = useMemo(() => {
        if (!hasAdvanced) return false;
        const baseBid = strategyData.generalBidAmount;
        const hasSpecBidOverride = Object.values(overrides.specBids).some((bid) => bid !== baseBid);
        const hasSpecCap = Object.keys(overrides.specCaps).length > 0;
        const hasDisabledSpec = overrides.disabledSpecs.size > 0;
        const hasProvBidOverride = Object.values(overrides.provBids).some((m) =>
            Object.values(m).some((bid) => bid !== baseBid),
        );
        const hasProvCap = Object.values(overrides.provCaps).some((m) => Object.keys(m).length > 0);
        const hasDisabledProvSpec = Object.values(overrides.disabledProvSpecs).some((set) => set.size > 0);
        return (
            hasSpecBidOverride ||
            hasSpecCap ||
            hasDisabledSpec ||
            hasProvBidOverride ||
            hasProvCap ||
            hasDisabledProvSpec
        );
    }, [hasAdvanced, strategyData.generalBidAmount, overrides]);

    // Whether the bid breakdown disclosure should show.
    // Only relevant when advanced settings are enabled.
    const showBreakdown = useMemo(() => {
        if (!hasAdvanced) return false;
        const someProvidersOff = budgetExcluded.size > 0;
        return someProvidersOff || hasSpecCustomizations;
    }, [hasAdvanced, budgetExcluded.size, hasSpecCustomizations]);

    // Summary title
    const summaryTitle = useMemo(() => {
        const activeCount = activeProviders.length;
        const totalCount = stdListingCount;
        const allOn = activeCount === totalCount;

        let title = allOn
            ? `Applies to all ${activeCount} providers`
            : `Applies to ${activeCount} of ${totalCount} providers`;

        if (hasAdvanced && activeSpecCount > 0) {
            title += ` across ${activeSpecCount} ${activeSpecCount === 1 ? 'specialty' : 'specialties'}`;
        }

        return title;
    }, [activeProviders.length, stdListingCount, hasAdvanced, activeSpecCount]);

    // Credit
    const creditPerProvider = off || 150;
    const totalCredit = creditPerProvider * activeProviders.length;

    const today = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

    const handleCancel = () => {
        setAgreed(false);
        setBreakdownOpen(false);
        onCancel();
    };

    const handleConfirm = async () => {
        if (!agreed) return;
        setLoading(true);
        try {
            const result = await savePracticePoolBudget(
                buildPracticeSaveRequest(strategyData, providers, budgetExcluded, overrides),
            );
            if (!result.error && result.data?.data) {
                onConfirm();
            }
            handleCancel();
        } catch (error) {
            console.error('Confirm failed:', error);
        } finally {
            setLoading(false);
        }
    };

    const renderBidBreakdown = () => {
        if (!showBreakdown) return null;

        // Provider on/off only (no bid/spec customizations)
        if (!hasSpecCustomizations) {
            return (
                <div className={s.disclosure}>
                    <button
                        type="button"
                        className={s.disclosureToggle}
                        onClick={() => setBreakdownOpen(!breakdownOpen)}
                    >
                        Bid breakdown
                        <span className={`${s.disclosureArrow} ${breakdownOpen ? s.disclosureArrowOpen : ''}`} />
                    </button>
                    {breakdownOpen && (
                        <div className={s.disclosureBody}>
                            {providers.map((p) => {
                                const isOff = budgetExcluded.has(p.providerId);
                                return (
                                    <div
                                        key={p.providerId}
                                        className={`${s.disclosureRow} ${isOff ? s.disclosureRowMuted : ''}`}
                                    >
                                        <span className={s.k}>{p.name}</span>
                                        <span className={s.v}>{isOff ? 'Not running' : 'On'}</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            );
        }

        // Advanced settings: show all specialties
        return (
            <div className={s.disclosure}>
                <button type="button" className={s.disclosureToggle} onClick={() => setBreakdownOpen(!breakdownOpen)}>
                    Bid breakdown
                    <span className={`${s.disclosureArrow} ${breakdownOpen ? s.disclosureArrowOpen : ''}`} />
                </button>
                {breakdownOpen && (
                    <div className={s.disclosureBody}>
                        {specGroups.map((g) => {
                            const isSpecOff = overrides.disabledSpecs.has(g.key);
                            if (isSpecOff) {
                                return (
                                    <div key={g.key} className={`${s.disclosureRow} ${s.disclosureRowMuted}`}>
                                        <span className={s.k}>{g.name}</span>
                                        <span className={s.v}>Not running</span>
                                    </div>
                                );
                            }

                            const specBid = overrides.specBids[g.name] ?? strategyData.generalBidAmount;
                            const specCap = overrides.specCaps[g.name];
                            const specProvBids = overrides.provBids[g.key];
                            const isProvInactive = (id: number) =>
                                budgetExcluded.has(id) || (overrides?.disabledProvSpecs[g.key]?.has(id) ?? false);
                            const hasProvOverrides =
                                specProvBids && g.providers.some((p) => specProvBids[p.providerId] !== undefined);
                            // Expand into per-provider rows when there are provider-level
                            // bid overrides OR any inactive provider that must be surfaced.
                            const shouldExpand =
                                hasProvOverrides || g.providers.some((p) => isProvInactive(p.providerId));

                            const capText = specCap ? ` · $${specCap}/${periodLabel} cap` : '';

                            if (shouldExpand) {
                                return (
                                    <React.Fragment key={g.key}>
                                        <div className={s.disclosureGroup}>
                                            <span className={s.k}>{g.name}</span>
                                            <span className={s.v}>
                                                ${specBid}
                                                {capText}
                                            </span>
                                        </div>
                                        {g.providers.map((p) => {
                                            const isProvOff = isProvInactive(p.providerId);
                                            const provBid = specProvBids?.[p.providerId] ?? specBid;
                                            const provCap = overrides.provCaps[g.key]?.[p.providerId];
                                            const provCapText = provCap ? ` · $${provCap}/${periodLabel} cap` : '';
                                            return (
                                                <div
                                                    key={p.providerId}
                                                    className={`${s.disclosureRow} ${s.disclosureRowSub} ${isProvOff ? s.disclosureRowMuted : ''}`}
                                                >
                                                    <span className={s.k}>{p.name}</span>
                                                    <span className={s.v}>
                                                        {isProvOff ? 'Not running' : `$${provBid}${provCapText}`}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </React.Fragment>
                                );
                            }

                            return (
                                <div key={g.key} className={s.disclosureRow}>
                                    <span className={s.k}>{g.name}</span>
                                    <span className={s.v}>
                                        ${specBid}
                                        {capText}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    const budgetDisplay = `$${(strategyData.budgetCap || 0).toLocaleString()} ${periodText}`;
    const showPerProvider = activeProviders.length < providers.length && activeProviders.length > 0;
    const perProviderBudget = activeProviders.length
        ? Math.round((strategyData.budgetCap || 0) / activeProviders.length)
        : 0;

    return (
        <>
            <Modal
                open={open}
                onCancel={handleCancel}
                footer={null}
                width={603}
                className={s.modal}
                closeIcon={<CloseOutlined className={s.closeIcon} />}
            >
                <div className={s.content}>
                    {/* Header */}
                    <div className={s.header}>
                        <div className={s.logo}>
                            <img src={KlarityLogo} alt="Klarity" className={s.logoImg} />
                        </div>
                        <div>
                            <p className={s.title}>Confirm your Pro-Listings campaign</p>
                            <p className={s.subtitle}>Review your setup before launching</p>
                        </div>
                    </div>

                    {/* Credit */}
                    <div className={s.creditSection}>
                        <svg className={s.creditIcon} viewBox="0 0 20 20" fill="none">
                            <circle cx="10" cy="10" r="8" fill="#34D399" />
                            <polyline
                                points="6.5 10 9 12.5 13.5 7.5"
                                stroke="#fff"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                fill="none"
                            />
                        </svg>
                        <div>
                            <p className={s.creditTitle}>
                                <strong>${creditPerProvider} free credit per provider</strong> automatically applied!
                            </p>
                            <p className={s.creditText}>
                                Get ${creditPerProvider} off each provider&apos;s first Pro-Listings campaign &mdash;{' '}
                                <strong>${totalCredit.toLocaleString()} total</strong> across your{' '}
                                {activeProviders.length} active providers. You&apos;ll only be charged after the credit
                                is used.
                            </p>
                        </div>
                    </div>

                    {/* Summary card */}
                    <div className={s.summaryCard}>
                        <p className={s.summaryTitle}>{summaryTitle}</p>
                        <div className={s.summaryRows}>
                            <div className={s.summaryRow}>
                                <span className={s.summaryLabel}>Bid amount</span>
                                <span className={s.summaryValue}>
                                    {bidVaries
                                        ? `$${bidRange.min}–$${bidRange.max} per booking`
                                        : `$${bidRange.min} per booking`}
                                    {bidVaries && <span className={s.variesTag}>{variesReason}</span>}
                                </span>
                            </div>
                            <div className={s.summaryRow}>
                                <span className={s.summaryLabel}>Budget amount</span>
                                <span className={s.summaryValue}>
                                    {budgetDisplay}
                                    {showPerProvider && (
                                        <span className={s.summaryValueSub}>
                                            &asymp; ${perProviderBudget.toLocaleString()} per active provider
                                        </span>
                                    )}
                                </span>
                            </div>
                        </div>
                        {renderBidBreakdown()}
                    </div>

                    {/* Agreement */}
                    <div className={s.agreementSection}>
                        <Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)}>
                            <span className={s.agreementText}>
                                By checking this box, I agree to the{' '}
                                <a
                                    href="#"
                                    className={s.agreementLink}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setShowAgreement(true);
                                    }}
                                >
                                    Klarity Health Pro-Listings Agreement
                                </a>{' '}
                                outlined in the agreement on {today}.
                            </span>
                        </Checkbox>
                    </div>

                    {/* Actions */}
                    <Button
                        type="primary"
                        size="large"
                        block
                        disabled={!agreed}
                        loading={loading}
                        onClick={handleConfirm}
                        className={s.confirmButton}
                    >
                        Confirm
                    </Button>
                    <p className={s.footnote}>
                        By confirming, your settings will go live immediately. Charges only apply when a new patient
                        books through your listing.
                    </p>
                </div>
            </Modal>

            {/* Agreement iframe modal */}
            <Modal
                open={showAgreement}
                onCancel={() => setShowAgreement(false)}
                footer={null}
                width={600}
                className={s.modal}
            >
                <div onClick={() => setShowAgreement(false)} className={s.backIcon}>
                    <LeftOutlined />
                </div>
                <iframe className={s.iframe} title="overview" src={PlatformAgreementUrl} />
            </Modal>
        </>
    );
};

export default GroupConfirmSaveModal;
